// src/components/api/index.js
import { getValidAccessToken } from "../auth/renewToken";
import { ROUTES } from "../../api/routes";

const {
  REACT_APP_API_URL = "https://esbdev.premier-research.com/",
  REACT_APP_API_URL_DEV = "https://esbdev.premier-research.com/",
  REACT_APP_API_URL_TEST = "https://esbdev.premier-research.com/",
  REACT_APP_API_URL_PROD = "https://esbdev.premier-research.com/",
} = process.env;

/**
 * Inject MSAL instance once from App.js:
 *   setMsalContext({ instance })
 *
 * Do not cache access tokens here.
 * Always ask MSAL for the current valid token at request time.
 */
let msalInstance = null;

export function setMsalContext({ instance }) {
  msalInstance = instance || null;
}

function resolveBaseUrl(environment) {
  if (environment === "dev") return REACT_APP_API_URL_DEV;
  if (environment === "test") return REACT_APP_API_URL_TEST;
  if (environment === "prod") return REACT_APP_API_URL_PROD;
  return REACT_APP_API_URL;
}

async function fetchJson(response) {
  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  return isJson ? response.json() : null;
}

async function fetchJsonOrEmpty(response) {
  if (response.status === 200) return response.json();
  return [];
}

/**
 * Builds headers with a current valid Bearer token from MSAL.
 * Never reads access_token from sessionStorage.
 * Never sends "Bearer null".
 */
async function buildAuthHeaders(extraHeaders = {}) {
  const headers = new Headers(extraHeaders);

  let token = null;

  if (msalInstance) {
    try {
      token = await getValidAccessToken(msalInstance);
    } catch (error) {
      console.error("Failed to acquire valid access token:", error);
    }
  } else {
    console.warn("MSAL instance not available at request time.");
  }

  if (token && token.length > 10) {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    console.warn(
      "No valid access token available at request time (Authorization omitted).",
    );
  }

  return headers;
}

async function doFetch(url, options = {}, extraHeaders = {}) {
  const headers = await buildAuthHeaders(extraHeaders);
  return fetch(url, { ...options, headers });
}

/** ------------------- GET APIs ------------------- **/

export async function GetDataFromAPI(resourcePath) {
  const URL = REACT_APP_API_URL + resourcePath;
  const response = await doFetch(URL, { method: "GET" });
  return fetchJsonOrEmpty(response);
}

export async function GetDataFromAPIwithEnv(resourcePath, environment) {
  const URL = resolveBaseUrl(environment) + resourcePath;
  const response = await doFetch(URL, { method: "GET" });
  return response.json();
}

export async function GetDataFromAllEnv(resourcePath) {
  const [devRes, testRes, prodRes] = await Promise.all([
    doFetch(REACT_APP_API_URL_DEV + resourcePath, { method: "GET" }),
    doFetch(REACT_APP_API_URL_TEST + resourcePath, { method: "GET" }),
    doFetch(REACT_APP_API_URL_PROD + resourcePath, { method: "GET" }),
  ]);

  const [dev_data, test_data, prod_data] = await Promise.all([
    devRes.json(),
    testRes.json(),
    prodRes.json(),
  ]);

  return [dev_data, test_data, prod_data];
}

/** ------------------- Write helpers ------------------- **/

/**
 * Generic write handler — shared by all POST/PATCH operations.
 * Returns { code, title, message } or rejects on HTTP error.
 */
async function writeToAPI(resourcePath, payload, method = "POST", env = null) {
  const base = env ? resolveBaseUrl(env.toLowerCase()) : REACT_APP_API_URL;
  const URL = base + resourcePath;

  try {
    const response = await doFetch(
      URL,
      {
        method,
        body: payload !== undefined ? JSON.stringify(payload, null, 2) : undefined,
      },
      payload !== undefined ? { "Content-Type": "application/json" } : {},
    );

    const data = await fetchJson(response);

    if (!response.ok) {
      return Promise.reject((data && data.message) || response.status);
    }

    if (data?.returnCode === 200 || response.status === 200 || response.ok) {
      return { code: 200, title: "Result", message: "Success!" };
    }

    return {
      code: 500,
      title: "Error",
      message: data?.returnMessage || "Unknown error",
    };
  } catch (error) {
    console.error(error);
    return { code: 500, title: "Error", message: error.toString() };
  }
}

/** ------------------- POST / PATCH APIs ------------------- **/

/** Add a record to the default environment. */
export async function AddRecord(resourcePath, payload) {
  const result = await writeToAPI(resourcePath, payload, "POST");
  if (result.code === 200) {
    return { ...result, message: "Record Added Successfully!" };
  }
  return result;
}

/** Add a record to a specific environment (dev / test / prod). */
export async function AddRecordToOtherEnv(resourcePath, env, payload) {
  const result = await writeToAPI(resourcePath, payload, "POST", env);
  if (result.code === 200) {
    return { ...result, message: "Record Added Successfully!" };
  }
  return result;
}

/** Update a record via POST (API convention for updates). */
export async function UpdateRecord(resourcePath, payload) {
  const result = await writeToAPI(resourcePath, payload, "POST");
  if (result.code === 200) {
    return { ...result, message: "Record Updated Successfully!" };
  }
  return result;
}

/** Update a record via PATCH with a payload body. */
export async function UpdateOuraPatient(resourcePath, payload) {
  const result = await writeToAPI(resourcePath, payload, "PATCH");
  if (result.code === 200) {
    return { ...result, message: "Record Updated Successfully!" };
  }
  return result;
}

/** Update a record via PATCH using only the URL (no body). */
export async function UpdateRecordByPath(resourcePath) {
  const result = await writeToAPI(resourcePath, undefined, "PATCH");
  if (result.code === 200) {
    return { ...result, message: "Record Updated Successfully!" };
  }
  return result;
}

/**
 * AddOuraPatient is identical to AddRecord — kept as a named alias
 * so existing call sites don't need to change.
 */
export async function AddOuraPatient(resourcePath, payload) {
  return AddRecord(resourcePath, payload);
}

/** Trigger a GET that clears/refreshes a cache entry. */
export async function ClearCache(resourcePath) {
  const URL = REACT_APP_API_URL + resourcePath;

  try {
    const response = await doFetch(URL, { method: "GET" });
    const data = await fetchJson(response);

    if (!response.ok) {
      return Promise.reject((data && data.message) || response.status);
    }

    if (response.status === 200) {
      return { code: 200, title: "Result", message: "Refresh Successful!" };
    }

    return {
      code: 500,
      title: "Error",
      message: data?.returnMessage || "Unknown error",
    };
  } catch (error) {
    console.error(error);
    return error.toString();
  }
}

export async function GetCacheListEI(resourcePath) {
  const URL = REACT_APP_API_URL + resourcePath;

  try {
    const response = await doFetch(URL, { method: "GET" });
    return response.json();
  } catch (err) {
    console.error(err);
  }
}

/** Reprocess Remarque events via a GET trigger. */
export async function ProcessRemarqueEvents(resourcePath) {
  const result = await writeToAPI(resourcePath, undefined, "GET");
  if (result.code === 200) {
    return { ...result, message: "Reprocess Successfully!" };
  }
  return result;
}

/** Run a RedHat process via POST. */
export async function RunRedHatProcess(resourcePath, payload) {
  const result = await writeToAPI(resourcePath, payload, "POST");
  if (result.code === 200) {
    return { ...result, message: "Record Added Successfully!" };
  }
  return result;
}

/** ------------------- Download APIs ------------------- **/

async function downloadBlob(url) {
  const response = await doFetch(url, { method: "GET" });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  return response.blob();
}

export async function DownloadEventKickoutFile(eventId) {
  return downloadBlob(REACT_APP_API_URL + ROUTES.kickouts.eventFile(eventId));
}

export async function DownloadErrorEventFile(pkError) {
  return downloadBlob(REACT_APP_API_URL + ROUTES.errorLogEventFile(pkError));
}

// Alias kept for backward compatibility
export const downloadErrorEventXml = DownloadErrorEventFile;
